import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { WalteroCard, WalteroTypography } from '../waltero';
import { useSharedPerformanceCache } from '../../context/SharedPerformanceCache';
import * as echarts from 'echarts';

/**
 * Hourly Connectivity Histogram - Transmission activity by hour
 * 
 * Shows distribution of device transmission activity across 24-hour periods.
 * Displays average daily transmission counts by hour of day.
 */
const HourlyConnectivityHistogram = ({ selectedOrgId, startDate, endDate }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  
  // Use shared performance cache
  const { loadAllPerformanceData, getEndpointData, getDevices, cacheInfo } = useSharedPerformanceCache();
  
  const [hourlyData, setHourlyData] = useState(Array.from({ length: 24 }, (_, hour) => ({
    hour,
    totalTransmissions: 0,
    totalActiveDays: 0,
    deviceCount: 0,
    averageDailyTransmissions: 0,
    label: `${hour.toString().padStart(2, '0')}:00`
  })));

  useEffect(() => {
    loadHourlyData();
  }, [selectedOrgId, startDate, endDate]);

  useEffect(() => {
    // Initialize ECharts instance
    if (chartRef.current && !chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
      updateChart();
    }
    
    // Update chart when data changes
    if (chartInstanceRef.current) {
      updateChart();
    }
    
    // Cleanup on unmount
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [hourlyData, cacheInfo.devicesLoaded, cacheInfo.endpointsLoaded]);

  const loadHourlyData = async () => {
    if (!selectedOrgId || !startDate || !endDate) return;
    
    // Reset hourly data
    const initialHourlyData = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      totalTransmissions: 0,
      totalActiveDays: 0,
      deviceCount: 0,
      averageDailyTransmissions: 0,
      label: `${hour.toString().padStart(2, '0')}:00`
    }));
    setHourlyData(initialHourlyData);
    
    try {
      loadAllPerformanceData(selectedOrgId, startDate, endDate);

    } catch (err) {
      console.error('❌ Error loading hourly data:', err);
    }
  };
  
  // Update chart data whenever cache changes  
  useEffect(() => {
    if (!selectedOrgId || !startDate || !endDate || getDevices().length === 0) return;
    
    // Reset hourly data
    const initialHourlyData = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      totalTransmissions: 0,
      totalActiveDays: 0,
      deviceCount: 0,
      averageDailyTransmissions: 0,
      label: `${hour.toString().padStart(2, '0')}:00`
    }));
    
    const devices = getDevices();
    
    // Calculate number of days in the date range for normalization
    const daysDifference = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;

    // Process each device's connectivity-pattern data from cache
    const newHourlyData = [...initialHourlyData];
    
    devices.forEach(device => {
      const connectivityData = getEndpointData('connectivity-pattern', device.id);
      
      if (connectivityData?.hourlyPattern) {
        connectivityData.hourlyPattern.forEach(hourData => {
          const hourIndex = hourData.hour;
          if (hourIndex >= 0 && hourIndex <= 23) {
            newHourlyData[hourIndex].totalTransmissions += hourData.transmissionCount || 0;
            if (hourData.transmissionCount > 0) {
              newHourlyData[hourIndex].deviceCount += 1;
            }
            
            // Calculate normalized average daily transmissions for this hour
            newHourlyData[hourIndex].averageDailyTransmissions = 
              Math.round((newHourlyData[hourIndex].totalTransmissions / daysDifference) * 10) / 10;
          }
        });
        
      }
    });

    setHourlyData(newHourlyData);
  }, [selectedOrgId, startDate, endDate, cacheInfo.endpointsLoaded, getEndpointData, getDevices]);

  const updateChart = () => {
    if (!chartInstanceRef.current) return;
    
    const option = {
      title: {
        text: 'Hourly Connectivity Distribution - Average Daily',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
          color: '#2c3e50'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          const data = params[0];
          const hourData = hourlyData[data.dataIndex];
          const totalDays = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;
          return `
            <strong>${hourData.label}</strong><br/>
            Avg Daily Transmissions: <strong>${hourData.averageDailyTransmissions}</strong><br/>
            Total Transmissions: <strong>${hourData.totalTransmissions.toLocaleString()}</strong><br/>
            Active Devices: <strong>${hourData.deviceCount}</strong><br/>
            Date Range: <strong>${totalDays} days</strong>
          `;
        }
      },
      xAxis: {
        type: 'category',
        data: hourlyData.map(h => h.label),
        axisLabel: {
          interval: 2, // Show every 3rd hour label
          fontSize: 10
        },
        name: 'Hour of Day',
        nameLocation: 'center',
        nameGap: 30
      },
      yAxis: {
        type: 'value',
        name: 'Average Daily Transmissions',
        nameLocation: 'center',
        nameGap: 50,
        axisLabel: {
          formatter: function(value) {
            if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'k';
            }
            return value.toFixed(1);
          }
        }
      },
      series: [{
        name: 'Average Daily Transmissions',
        type: 'bar',
        data: hourlyData.map(h => h.averageDailyTransmissions),
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#4fc3f7' },
            { offset: 1, color: '#1976d2' }
          ])
        },
        emphasis: {
          itemStyle: {
            color: '#0d47a1'
          }
        }
      }],
      grid: {
        left: '60px',
        right: '30px',
        top: '80px',
        bottom: '60px'
      }
    };

    chartInstanceRef.current.setOption(option);
  };

  return (
    <WalteroCard sx={{ p: 3, mt: 3 }}>
      <WalteroTypography variant="h6" sx={{ mb: 2 }}>
        Hourly Connectivity Distribution - Average Daily (0-23 Hours)
      </WalteroTypography>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
Device transmission activity by hour of day. Shows average daily transmission counts distributed across 24-hour periods.
      </Typography>


      <Box 
        ref={chartRef} 
        sx={{ 
          width: '100%', 
          height: '400px',
          minHeight: '400px'
        }} 
      />

      {/* Summary */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: '600' }}>
          Total Transmissions: {hourlyData.reduce((sum, hour) => sum + hour.totalTransmissions, 0).toLocaleString()}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Peak Hour (Avg Daily): {hourlyData.reduce((max, current) => 
            current.averageDailyTransmissions > max.averageDailyTransmissions ? current : max
          ).label} ({hourlyData.reduce((max, current) => 
            current.averageDailyTransmissions > max.averageDailyTransmissions ? current : max
          ).averageDailyTransmissions} avg daily transmissions)
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Normalization: Total transmissions ÷ {Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1} days
        </Typography>
      </Box>
    </WalteroCard>
  );
};

export default HourlyConnectivityHistogram;